import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Box, Fade, useTheme } from '@material-ui/core';
import { useHistory, useLocation } from 'react-router-dom';
import { Thread, Details, Notes } from './Conversation';
import { ThreadList } from './Threads';
import { EmptyChat } from './MainView';
import { CallsView } from './Calls';
import { useWarboxConversation, useWarboxMessage } from 'src/hooks';
import useAuth from 'src/hooks/useAuth';
import { channel } from 'src/service/pusher';
import { getNotificationArea } from 'src/components/Alerts/utils/getNotificationArea';
import { USER_LEVEL_ACCESS } from 'src/components/access';
import { ApiV2 } from 'src/api/apiv2';
import { HEADERS } from 'src/constants/headers';
import type { ConversationView, ThreadMessage, ThreadSummary } from '../types';
import type { TDetailsTab } from './Conversation/Details';
import {
    mapConversationsToThreads,
    mapMessagesToThreadMessages,
} from '../utils';
import {
    buildConversationMessageFilters,
    canReceiveConversationMessage,
} from '../utils/noteConversationPermissions';
import {
    ARCHIVED_CONVERSATION_STATUS,
    buildOmnichannelConversationsQuery,
    type TConversationListFilter,
} from '../constants/conversationStatus';
import {
    DEFAULT_CONVERSATION_ADVANCED_FILTERS,
    buildConversationFiltersPayload,
    type TConversationAdvancedFilters,
} from '../constants/conversationAdvancedFilters';
import {
    getOmnichannelAreaOptions,
    resolveOmnichannelAreaFilter,
    shouldShowOmnichannelAreaFilter,
} from '../utils/omnichannelAreaFilter';

const OMNICHANNEL_CONVERSATIONS_UPDATED = 'omnichannel:conversations:updated';
const MESSAGES_PAGE_LIMIT = 30;

const getOmnichannelConversationsStoreAreaUpdatedEvent = ({
    storeId,
    area,
}: {
    storeId: string;
    area: string;
}) => `omnichannel:conversations:${storeId}:${area}:updated`;

const getOmnichannelConversationsUserUpdatedEvent = (userId: string) =>
    `omnichannel:conversations:user:${userId}:updated`;

const getOmnichannelConversationUpdatedEvent = (conversationId: string) =>
    `omnichannel:conversation:${conversationId}:updated`;

const getStoreId = (store: any) => store?._id || store;

const getConversationsEventsByUser = (user: any): string[] => {
    if (user?.accessLevel === USER_LEVEL_ACCESS && user?._id) {
        return [getOmnichannelConversationsUserUpdatedEvent(user._id)];
    }

    const stores = user?.stores || [];
    const areas = user?.areas || [];
    const events = stores.flatMap((store: any) => {
        const storeId = getStoreId(store);
        if (!storeId) return [];

        return areas.flatMap((userArea: string) =>
            getNotificationArea(userArea as any, user?.carType).map(
                (area: string) =>
                    getOmnichannelConversationsStoreAreaUpdatedEvent({
                        storeId,
                        area,
                    })
            )
        );
    });

    return events.length
        ? Array.from(new Set(events))
        : [OMNICHANNEL_CONVERSATIONS_UPDATED];
};

export const Conversations = () => {
    const [view, setView] = useState<ConversationView>('empty');
    const [detailsInitialTab, setDetailsInitialTab] =
        useState<TDetailsTab>('info');
    const [selectedThreadId, setSelectedThreadId] = useState<string | null>(
        null
    );
    const [listFilter, setListFilter] =
        useState<TConversationListFilter>('active');
    const [selectedArea, setSelectedArea] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [advancedFilters, setAdvancedFilters] =
        useState<TConversationAdvancedFilters>(
            DEFAULT_CONVERSATION_ADVANCED_FILTERS
        );
    const theme = useTheme();
    const history = useHistory();
    const location = useLocation();
    const { user } = useAuth();
    const availableAreas = useMemo(
        () => getOmnichannelAreaOptions(user),
        [user]
    );
    const showAreaFilter = shouldShowOmnichannelAreaFilter(availableAreas);

    useEffect(() => {
        if (selectedArea && !availableAreas.includes(selectedArea)) {
            setSelectedArea('');
        }

        setAdvancedFilters((prev) => {
            const nextAreas = (prev.areas || []).filter((area) =>
                availableAreas.includes(area.value)
            );
            if (nextAreas.length === (prev.areas || []).length) return prev;
            return { ...prev, areas: nextAreas };
        });
    }, [availableAreas, selectedArea]);

    const areaFilter = useMemo(
        () =>
            resolveOmnichannelAreaFilter({
                user,
                availableAreas,
                selectedArea,
                advancedAreas: advancedFilters.areas.map((item) => item.value),
            }),
        [user, availableAreas, selectedArea, advancedFilters.areas]
    );
    const {
        conversations,
        getConversations,
        upsertConversation,
        loading: isLoadingConversations,
    } = useWarboxConversation();
    const {
        messages,
        total: totalMessages,
        loading: isLoadingMessages,
        getMessagesByConversation,
        upsertMessage,
        clearState: clearMessages,
    } = useWarboxMessage();
    const [messagesPage, setMessagesPage] = useState(1);
    const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false);
    const isLoadingMoreMessagesRef = useRef(false);
    const queryParams = useMemo(
        () => new URLSearchParams(location.search),
        [location.search]
    );
    const conversationId = queryParams.get('conversationId');

    const rawThreads = useMemo(
        () => mapConversationsToThreads(conversations),
        [conversations]
    );
    const threads = useMemo(
        () =>
            rawThreads.map((thread) =>
                thread._id === selectedThreadId
                    ? {
                          ...thread,
                          isRead: true,
                          lastMessage: {
                              ...thread.lastMessage,
                              isSeen: true,
                          },
                      }
                    : thread
            ),
        [rawThreads, selectedThreadId]
    );
    const selectedThread =
        threads.find((thread) => thread._id === selectedThreadId) || null;
    const selectedConversation = useMemo(() => {
        if (!selectedThread) return null;

        const rawConversation = conversations?.find(
            (conversation: any) =>
                conversation?._id === selectedThread._id ||
                conversation?.id === selectedThread._id
        );

        return {
            ...selectedThread,
            rawConversation,
            messages: mapMessagesToThreadMessages(messages),
        };
    }, [conversations, messages, selectedThread]);
    const conversationsQuery = useMemo(
        () =>
            buildOmnichannelConversationsQuery(listFilter, {
                filters: buildConversationFiltersPayload({
                    listFilter,
                    advancedFilters,
                    query: searchQuery,
                    area: areaFilter,
                }),
            }),
        [listFilter, advancedFilters, searchQuery, areaFilter]
    );
    const conversationMessageFilters = useMemo(
        () =>
            buildConversationMessageFilters(user, {
                limit: MESSAGES_PAGE_LIMIT,
            }),
        [user]
    );

    useEffect(() => {
        getConversations(conversationsQuery);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationsQuery]);

    const clearSelectedConversation = useCallback(() => {
        setSelectedThreadId(null);
        setView('empty');

        const params = new URLSearchParams(location.search);
        params.delete('conversationId');
        history.push({
            pathname: '/omnichannel',
            search: params.toString(),
        });
    }, [history, location.search]);

    const clearFiltersNavigationState = useCallback(() => {
        setSelectedThreadId(null);
        setView('empty');

        if (!location.search) return;

        history.replace({
            pathname: location.pathname,
            search: '',
        });
    }, [history, location.pathname, location.search]);

    const handleSearchChange = (value: string) => {
        setSearchInput(value);
    };

    const handleSearch = () => {
        setSearchQuery(searchInput.trim());
        clearFiltersNavigationState();
    };

    const handleOutboundMessageSent = useCallback(() => {
        getConversations(conversationsQuery);
    }, [conversationsQuery, getConversations]);

    const handleListFilterChange = (filter: TConversationListFilter) => {
        setListFilter(filter);
        setAdvancedFilters((prev) =>
            prev.status ? { ...prev, status: '' } : prev
        );
        clearFiltersNavigationState();
    };

    const handleAreaFilterChange = (area: string) => {
        setSelectedArea(area);
        clearFiltersNavigationState();
    };

    const handleAdvancedFiltersChange = (
        filters: TConversationAdvancedFilters
    ) => {
        setAdvancedFilters(filters);
        clearFiltersNavigationState();

        if (filters.status === 'archived') {
            setListFilter('archived');
        } else if (
            filters.status === 'active' ||
            filters.status === 'blocked'
        ) {
            setListFilter('active');
        }
    };

    const handleConversationStatusUpdated = useCallback(
        async (status: string) => {
            await getConversations(conversationsQuery);

            const shouldClearSelection =
                (listFilter === 'active' &&
                    status === ARCHIVED_CONVERSATION_STATUS) ||
                (listFilter === 'archived' &&
                    status !== ARCHIVED_CONVERSATION_STATUS);

            if (shouldClearSelection) {
                clearSelectedConversation();
            }
        },
        [
            clearSelectedConversation,
            conversationsQuery,
            getConversations,
            listFilter,
        ]
    );

    useEffect(() => {
        if (!conversationId) {
            setSelectedThreadId(null);
            setView('empty');
            return;
        }

        if (selectedThreadId === conversationId) return;

        setSelectedThreadId(conversationId);
        setView('thread');
    }, [conversationId, selectedThreadId]);

    useEffect(() => {
        if (!selectedThreadId) return;

        setMessagesPage(1);
        isLoadingMoreMessagesRef.current = false;
        setIsLoadingMoreMessages(false);
        clearMessages();

        upsertConversation({
            _id: selectedThreadId,
            isRead: true,
        } as any);

        ApiV2.patch(
            `/conversations/${selectedThreadId}/read`,
            {},
            HEADERS()
        ).catch(() => undefined);

        getMessagesByConversation(selectedThreadId, {
            ...conversationMessageFilters,
            page: 1,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedThreadId, conversationMessageFilters]);

    const handleLoadMoreMessages = useCallback(async () => {
        if (!selectedThreadId || isLoadingMoreMessagesRef.current) return;
        if ((messages?.length || 0) >= (totalMessages || 0)) return;

        const nextPage = messagesPage + 1;

        isLoadingMoreMessagesRef.current = true;
        setIsLoadingMoreMessages(true);

        try {
            await getMessagesByConversation(
                selectedThreadId,
                {
                    ...conversationMessageFilters,
                    page: nextPage,
                },
                { mergeStrategy: 'prepend' }
            );
            setMessagesPage(nextPage);
        } finally {
            isLoadingMoreMessagesRef.current = false;
            setIsLoadingMoreMessages(false);
        }
    }, [
        getMessagesByConversation,
        conversationMessageFilters,
        messages?.length,
        messagesPage,
        selectedThreadId,
        totalMessages,
    ]);

    useEffect(() => {
        const conversationEvents = getConversationsEventsByUser(user);
        const handleConversationsUpdated = (event: any) => {
            const conversation = event?.payload?.conversation;
            if (conversation) upsertConversation(conversation);

            getConversations(conversationsQuery);
        };

        conversationEvents.forEach((eventName) => {
            channel.unbind(eventName);
            channel.bind(eventName, handleConversationsUpdated);
        });

        return () => {
            conversationEvents.forEach((eventName) => {
                channel.unbind(eventName);
            });
        };
    }, [conversationsQuery, user]);

    useEffect(() => {
        if (!selectedThreadId) return;

        const handleConversationUpdated = (event: any) => {
            const message = event?.payload?.message;
            if (message && canReceiveConversationMessage(message, user)) {
                upsertMessage(message);
            }
        };
        const conversationEvent =
            getOmnichannelConversationUpdatedEvent(selectedThreadId);

        channel.unbind(conversationEvent);
        channel.bind(conversationEvent, handleConversationUpdated);

        return () => {
            channel.unbind(conversationEvent);
        };
    }, [selectedThreadId, upsertMessage, user]);

    const handleThreadClick = (thread: ThreadSummary) => {
        setSelectedThreadId(thread._id);
        setView('thread');
        const params = new URLSearchParams(location.search);
        params.set('conversationId', thread._id);
        history.push({
            pathname: '/omnichannel',
            search: params.toString(),
        });
    };

    const conversationNotes = useMemo(
        () =>
            (messages as unknown as ThreadMessage[])
                ?.filter((message) => message.kind === 'note')
                .reverse() || [],
        [selectedConversation]
    );

    const renderView = () => {
        switch (view) {
            case 'notes':
                return selectedConversation ? (
                    <Notes
                        notes={conversationNotes}
                        onClose={() => setView('thread')}
                    />
                ) : (
                    <EmptyChat onMessageSent={handleOutboundMessageSent} />
                );

            case 'thread':
                return selectedConversation ? (
                    <Thread
                        conversation={selectedConversation}
                        onOpenDetails={() => {
                            setDetailsInitialTab('info');
                            setView('details');
                        }}
                        onOpenCallHistory={() => {
                            setDetailsInitialTab('calls');
                            setView('details');
                        }}
                        onOpenNotes={() => setView('notes')}
                        onLoadMoreMessages={handleLoadMoreMessages}
                        hasMoreMessages={
                            (messages?.length || 0) < (totalMessages || 0)
                        }
                        isLoadingMessages={isLoadingMessages}
                        isLoadingMoreMessages={isLoadingMoreMessages}
                        onConversationStatusUpdated={
                            handleConversationStatusUpdated
                        }
                        onCloseChat={clearSelectedConversation}
                    />
                ) : (
                    <EmptyChat onMessageSent={handleOutboundMessageSent} />
                );

            case 'details':
                return selectedConversation ? (
                    <Details
                        conversation={selectedConversation}
                        initialTab={detailsInitialTab}
                        onClose={() => setView('thread')}
                        onConversationDeleted={() => {
                            clearMessages();
                            clearSelectedConversation();
                        }}
                    />
                ) : (
                    <EmptyChat onMessageSent={handleOutboundMessageSent} />
                );

            case 'calls':
                return <CallsView onClose={() => setView('empty')} />;

            default:
                return <EmptyChat />;
        }
    };

    return (
        <Box
            style={{
                minHeight: 'calc(100vh - 80px)',
                borderTop: `1px solid ${theme.palette.divider}`,
                borderLeft: `1px solid ${theme.palette.divider}`,
                overflow: 'hidden',
                display: 'flex',
            }}
        >
            <ThreadList
                threads={threads}
                activeThreadId={selectedThreadId || undefined}
                listFilter={listFilter}
                searchQuery={searchInput}
                isLoading={isLoadingConversations}
                advancedFilters={advancedFilters}
                onSearchChange={handleSearchChange}
                onSearch={handleSearch}
                onAdvancedFiltersChange={handleAdvancedFiltersChange}
                onListFilterChange={handleListFilterChange}
                availableAreas={showAreaFilter ? availableAreas : []}
                selectedArea={selectedArea}
                onAreaFilterChange={handleAreaFilterChange}
                onThreadClick={handleThreadClick}
            />
            <Box style={{ width: '75%' }}>
                <Fade in timeout={600} key={view}>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            borderLeft: `1px solid ${theme.palette.divider}`,
                            height: 'calc(100vh - 80px)',
                        }}
                    >
                        {renderView()}
                    </div>
                </Fade>
            </Box>
        </Box>
    );
};
